import React, { FC, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Paper,
  Typography,
  InputAdornment,
  Divider,
  Autocomplete,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import QuantityField from './QuantityField';
import { formatQuantity } from '../utils/quantity';
import { roundDollars } from '../utils/money';
import {
  LineItem,
  InvoiceDiscount,
  applyLineDiscount,
  computeInvoiceDiscountAmount,
  calculateTax,
  getTaxableSubtotal,
  SERVICE_CATEGORIES,
  PRODUCT_CATEGORIES,
} from '../utils/invoiceMath';

export type { LineItem, InvoiceDiscount } from '../utils/invoiceMath';
export {
  applyLineDiscount,
  computeInvoiceDiscountAmount,
  getTaxableSubtotal,
  calculateTax,
} from '../utils/invoiceMath';

export const DISCOUNT_REASONS = [
  'Staff discount',
  'Referral',
  'Service comp/issue',
  'Promotional',
  'Other',
];

export interface CatalogOption {
  id: string;
  name: string;
  category: string;
  basePrice: number;
  unit?: string;
  aliases?: string[];
}

interface InvoiceLineItemsEditorProps {
  lineItems: LineItem[];
  onChange: (lineItems: LineItem[]) => void;
  readOnly?: boolean;
  catalogItems?: CatalogOption[];
  taxRate?: number;
  taxMode?: string;
  splitPercentage?: number;
  splitOwnerTotal?: number;
  // BILL-DISC: invoice-level discount row in the totals footer. The row is
  // editable only when onInvoiceDiscountChange is provided (the backend
  // rejects discount edits on already-split co-owned invoices).
  invoiceDiscount?: InvoiceDiscount | null;
  onInvoiceDiscountChange?: (discount: InvoiceDiscount | null) => void;
  // Gate the per-line discount affordance the same way.
  allowLineDiscounts?: boolean;
}

const CATEGORY_ORDER: Record<string, number> = {
  appointment: 0,
  procedure: 1,
  medication: 2,
  vaccination: 3,
  other: 4,
};

const CATEGORY_LABELS: Record<string, string> = {
  appointment: 'Appointments',
  procedure: 'Procedures',
  medication: 'Medications',
  vaccination: 'Vaccinations',
  other: 'Other',
};

// Split a stored reason back into dropdown + detail ("Referral: Dr. Smith").
const parseReason = (reason?: string): { pick: string; detail: string } => {
  if (!reason) return { pick: DISCOUNT_REASONS[0], detail: '' };
  const idx = reason.indexOf(': ');
  const head = idx >= 0 ? reason.slice(0, idx) : reason;
  if (DISCOUNT_REASONS.includes(head)) {
    return { pick: head, detail: idx >= 0 ? reason.slice(idx + 2) : '' };
  }
  return { pick: 'Other', detail: reason };
};

const buildReason = (pick: string, detail: string): string =>
  detail.trim() ? `${pick}: ${detail.trim()}` : pick;

const InvoiceLineItemsEditor: FC<InvoiceLineItemsEditorProps> = ({
  lineItems,
  onChange,
  readOnly = false,
  catalogItems = [],
  taxRate = 0,
  taxMode = 'ALL',
  splitPercentage,
  splitOwnerTotal,
  invoiceDiscount = null,
  onInvoiceDiscountChange,
  allowLineDiscounts = true,
}) => {
  // BILL-DISC popover state — one editor shared by line + invoice targets.
  const [discountAnchor, setDiscountAnchor] = useState<HTMLElement | null>(null);
  const [discountTarget, setDiscountTarget] = useState<number | 'invoice' | null>(null);
  const [draftType, setDraftType] = useState<'percentage' | 'fixed'>('percentage');
  const [draftValue, setDraftValue] = useState<string>('');
  const [draftReasonPick, setDraftReasonPick] = useState<string>(DISCOUNT_REASONS[0]);
  const [draftReasonDetail, setDraftReasonDetail] = useState<string>('');

  const openDiscountEditor = (
    e: React.MouseEvent<HTMLElement>,
    target: number | 'invoice',
  ) => {
    const existing = target === 'invoice'
      ? invoiceDiscount
      : lineItems[target]?.discountType
        ? {
            discountType: lineItems[target].discountType!,
            discountValue: lineItems[target].discountValue || 0,
            discountReason: lineItems[target].discountReason,
          }
        : null;
    setDraftType(existing?.discountType || 'percentage');
    setDraftValue(existing?.discountValue ? String(existing.discountValue) : '');
    const { pick, detail } = parseReason(existing?.discountReason);
    setDraftReasonPick(pick);
    setDraftReasonDetail(detail);
    setDiscountTarget(target);
    setDiscountAnchor(e.currentTarget);
  };

  const closeDiscountEditor = () => {
    setDiscountAnchor(null);
    setDiscountTarget(null);
  };

  const draftValueNum = Number(draftValue);
  const draftValid =
    Number.isFinite(draftValueNum) &&
    draftValueNum > 0 &&
    (draftType !== 'percentage' || draftValueNum <= 100);

  const applyDraftDiscount = () => {
    if (!draftValid || discountTarget === null) return;
    const reason = buildReason(draftReasonPick, draftReasonDetail);
    if (discountTarget === 'invoice') {
      onInvoiceDiscountChange?.({
        discountType: draftType,
        discountValue: draftValueNum,
        discountReason: reason,
      });
    } else {
      const updated = [...lineItems];
      updated[discountTarget] = applyLineDiscount({
        ...updated[discountTarget],
        discountType: draftType,
        discountValue: draftValueNum,
        discountReason: reason,
      });
      onChange(updated);
    }
    closeDiscountEditor();
  };

  const removeDiscount = () => {
    if (discountTarget === 'invoice') {
      onInvoiceDiscountChange?.(null);
    } else if (discountTarget !== null) {
      const updated = [...lineItems];
      const { discountType, discountValue, discountAmount, discountReason, ...rest } =
        updated[discountTarget];
      updated[discountTarget] = applyLineDiscount(rest as LineItem);
      onChange(updated);
    }
    closeDiscountEditor();
  };

  const handleAddLine = () => {
    const newLineItem: LineItem = {
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0,
      category: 'other',
    };
    onChange([...lineItems, newLineItem]);
  };

  const handleRemoveLine = (index: number) => {
    const updatedLineItems = lineItems.filter((_, i) => i !== index);
    onChange(updatedLineItems);
  };

  const handleLineItemChange = (
    index: number,
    field: keyof LineItem,
    value: string | number
  ) => {
    const updatedLineItems = [...lineItems];
    updatedLineItems[index] = {
      ...updatedLineItems[index],
      [field]: value,
    };

    // Auto-calculate total when quantity or unit price changes; a discounted
    // line recomputes its discountAmount off the new base too.
    if (field === 'quantity' || field === 'unitPrice') {
      updatedLineItems[index] = applyLineDiscount(updatedLineItems[index]);
    }

    onChange(updatedLineItems);
  };

  const handleCatalogSelect = (index: number, option: CatalogOption | string | null) => {
    if (!option) return;

    const updatedLineItems = [...lineItems];

    if (typeof option === 'string') {
      updatedLineItems[index] = {
        ...updatedLineItems[index],
        description: option,
      };
    } else {
      updatedLineItems[index] = applyLineDiscount({
        ...updatedLineItems[index],
        quantity: updatedLineItems[index].quantity || 1,
        description: option.name,
        unitPrice: option.basePrice,
        category: option.category,
      });
    }

    onChange(updatedLineItems);
  };

  // Sort catalog items by category for grouping
  const sortedCatalogItems = [...catalogItems].sort((a, b) => {
    const orderA = CATEGORY_ORDER[a.category] ?? 99;
    const orderB = CATEGORY_ORDER[b.category] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });

  // BILL-DISC calculation order: line discounts (inside item.total) →
  // invoice discount → tax on the discounted base → total.
  const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
  const invoiceDiscountAmount = computeInvoiceDiscountAmount(subtotal, invoiceDiscount);
  const taxAmount = calculateTax(lineItems, taxRate, taxMode, invoiceDiscountAmount);
  const total = roundDollars(subtotal - invoiceDiscountAmount + taxAmount);

  const isSelectiveTax = taxMode !== 'ALL' && taxMode !== 'NONE' && taxRate > 0;
  const taxableSubtotal = isSelectiveTax ? getTaxableSubtotal(lineItems, taxMode) : subtotal;

  const isItemTaxable = (item: LineItem): boolean => {
    if (taxMode === 'NONE' || taxRate <= 0) return false;
    if (taxMode === 'ALL') return true;
    const cat = item.category || 'other';
    const taxableCategories = taxMode === 'SERVICES_ONLY' ? SERVICE_CATEGORIES : PRODUCT_CATEGORIES;
    return taxableCategories.includes(cat);
  };

  const canEditInvoiceDiscount = !readOnly && !!onInvoiceDiscountChange;
  const canEditLineDiscounts = !readOnly && allowLineDiscounts;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight="medium">
          Invoice Line Items
        </Typography>
        {!readOnly && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddLine}
            variant="outlined"
            size="small"
          >
            Add Line Item
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ mb: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="center">
                Qty
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '20%' }} align="right">
                Unit Price
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '20%' }} align="right">
                Total
              </TableCell>
              {!readOnly && (
                <TableCell sx={{ fontWeight: 'bold', width: '5%' }} align="center">

                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 4 : 5} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary" variant="body2">
                    {catalogItems.length === 0 && !readOnly
                      ? 'No line items added yet. Click "Add Line Item" to get started. Tip: Add your services in Settings > Service Pricing for quick selection.'
                      : 'No line items added yet. Click "Add Line Item" to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              lineItems.map((item, index) => {
                // Task 8: warn (never block) when the line matches a catalog
                // item that resolves to $0 — mirrors the backend's non-blocking
                // no-price warn. Matched by name since LineItem doesn't carry
                // a catalog id; disappears once the description no longer
                // matches or the unit price is edited off $0.
                const matchedCatalogItem = catalogItems.find((c) => c.name === item.description);
                const showNoPriceHint = !readOnly && !!matchedCatalogItem && item.unitPrice === 0;
                const hasLineDiscount = !!item.discountType && (item.discountAmount || 0) > 0;
                const originalTotal = hasLineDiscount
                  ? roundDollars(item.total + (item.discountAmount || 0))
                  : item.total;
                return (
                <TableRow key={index} hover>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">{item.description}</Typography>
                    ) : catalogItems.length > 0 ? (
                      <Autocomplete
                        freeSolo
                        options={sortedCatalogItems}
                        groupBy={(option) =>
                          typeof option === 'string'
                            ? ''
                            : CATEGORY_LABELS[option.category] || option.category
                        }
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.name
                        }
                        renderOption={(props, option) => {
                          if (typeof option === 'string') return <li {...props}>{option}</li>;
                          return (
                            <li {...props}>
                              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                <Box>
                                  <Typography variant="body2">{option.name}</Typography>
                                  {option.unit && (
                                    <Typography variant="caption" color="textSecondary">
                                      {option.unit}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="body2" color="primary" fontWeight="medium" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                                  ${option.basePrice.toFixed(2)}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                        inputValue={item.description}
                        onInputChange={(_, newValue) => {
                          handleLineItemChange(index, 'description', newValue);
                        }}
                        onChange={(_, newValue) => {
                          handleCatalogSelect(index, newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Type to search services or enter custom description"
                            size="small"
                            variant="standard"
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                            }}
                          />
                        )}
                        size="small"
                        filterOptions={(options, { inputValue }) => {
                          const search = inputValue.toLowerCase();
                          const filtered = options.filter((option) => {
                            if (typeof option === 'string') return true;
                            if (option.name.toLowerCase().includes(search)) return true;
                            if (option.aliases?.some(alias => alias.toLowerCase().includes(search))) return true;
                            return false;
                          });
                          return filtered;
                        }}
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            padding: '0 !important',
                          },
                        }}
                      />
                    ) : (
                      <TextField
                        value={item.description}
                        onChange={(e) =>
                          handleLineItemChange(index, 'description', e.target.value)
                        }
                        placeholder="Service or product description"
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                        }}
                      />
                    )}
                    {showNoPriceHint && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        No price set — will bill $0
                      </Typography>
                    )}
                    {hasLineDiscount && item.discountReason && (
                      <Chip
                        label={item.discountReason}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {readOnly ? (
                      <Typography variant="body2">{formatQuantity(Number(item.quantity))}</Typography>
                    ) : (
                      // Hundredths (ADR-0038): "Atropine 0.53 x $1.54" bills $0.82.
                      <QuantityField
                        value={item.quantity}
                        onChange={(next) => handleLineItemChange(index, 'quantity', next ?? 0)}
                        size="small"
                        variant="standard"
                        inputProps={{ 'aria-label': 'Quantity', style: { textAlign: 'center' } }}
                        InputProps={{
                          disableUnderline: true,
                        }}
                        sx={{ width: '60px' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {readOnly ? (
                      <Typography variant="body2">${item.unitPrice.toFixed(2)}</Typography>
                    ) : (
                      <TextField
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) =>
                          handleLineItemChange(index, 'unitPrice', Number(e.target.value))
                        }
                        size="small"
                        variant="standard"
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          disableUnderline: true,
                        }}
                        sx={{ width: '100px' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {hasLineDiscount && (
                      <Typography
                        variant="caption"
                        sx={{ textDecoration: 'line-through', color: 'text.disabled', display: 'block', lineHeight: 1 }}
                      >
                        ${originalTotal.toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="body2" fontWeight="medium">
                      ${item.total.toFixed(2)}
                    </Typography>
                    {isSelectiveTax && item.total > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isItemTaxable(item) ? 'success.main' : 'text.disabled',
                          fontSize: '0.65rem',
                          lineHeight: 1,
                        }}
                      >
                        {isItemTaxable(item) ? 'taxed' : 'tax-free'}
                      </Typography>
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        {canEditLineDiscounts && (
                          <IconButton
                            size="small"
                            color={hasLineDiscount ? 'primary' : 'default'}
                            onClick={(e) => openDiscountEditor(e, index)}
                            title={hasLineDiscount ? 'Edit discount' : 'Add discount'}
                          >
                            <LocalOfferIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveLine(index)}
                          title="Remove line item"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals Section */}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Box sx={{ minWidth: '280px' }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">Subtotal:</Typography>
            <Typography variant="body2" fontWeight="medium">
              ${subtotal.toFixed(2)}
            </Typography>
          </Box>
          {/* BILL-DISC: invoice-level discount row */}
          {(invoiceDiscountAmount > 0 || canEditInvoiceDiscount) && (
            <Box display="flex" justifyContent="space-between" mb={1} alignItems="center">
              {invoiceDiscountAmount > 0 ? (
                <>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      Discount
                      {invoiceDiscount?.discountType === 'percentage'
                        ? ` (${invoiceDiscount.discountValue}%)`
                        : ''}
                      :
                    </Typography>
                    {invoiceDiscount?.discountReason && (
                      <Chip
                        label={invoiceDiscount.discountReason}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ height: 18, fontSize: '0.65rem', maxWidth: 140 }}
                      />
                    )}
                    {canEditInvoiceDiscount && (
                      <Button
                        size="small"
                        onClick={(e) => openDiscountEditor(e, 'invoice')}
                        sx={{ minWidth: 0, p: 0, fontSize: '0.7rem', textTransform: 'none' }}
                      >
                        edit
                      </Button>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight="medium" color="primary">
                    −${invoiceDiscountAmount.toFixed(2)}
                  </Typography>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    startIcon={<LocalOfferIcon sx={{ fontSize: '0.9rem !important' }} />}
                    onClick={(e) => openDiscountEditor(e, 'invoice')}
                    sx={{ p: 0, fontSize: '0.75rem', textTransform: 'none' }}
                  >
                    Add Discount
                  </Button>
                  <span />
                </>
              )}
            </Box>
          )}
          {isSelectiveTax && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Taxable ({taxMode === 'SERVICES_ONLY' ? 'services' : 'products'}):
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ${taxableSubtotal.toFixed(2)}
              </Typography>
            </Box>
          )}
          {taxRate > 0 && taxMode !== 'NONE' && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Tax ({taxRate}%):</Typography>
              <Typography variant="body2" fontWeight="medium">
                ${taxAmount.toFixed(2)}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body1" fontWeight="bold">
              Total:
            </Typography>
            <Typography variant="body1" fontWeight="bold" color={splitPercentage != null ? undefined : 'primary'}>
              ${total.toFixed(2)}
            </Typography>
          </Box>
          {splitPercentage != null && splitOwnerTotal != null && (
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography variant="body1" fontWeight="bold">
                Share ({splitPercentage}%):
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="primary">
                ${splitOwnerTotal.toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* BILL-DISC: shared discount editor (line + invoice level) */}
      <Popover
        open={!!discountAnchor}
        anchorEl={discountAnchor}
        onClose={closeDiscountEditor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" mb={1}>
            {discountTarget === 'invoice' ? 'Invoice Discount' : 'Line Item Discount'}
          </Typography>
          <Box display="flex" gap={1} mb={1.5} alignItems="center">
            <ToggleButtonGroup
              value={draftType}
              exclusive
              size="small"
              onChange={(_, v) => v && setDraftType(v)}
            >
              <ToggleButton value="percentage" sx={{ px: 1.5 }}>%</ToggleButton>
              <ToggleButton value="fixed" sx={{ px: 1.5 }}>$</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              autoFocus
              type="number"
              size="small"
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              placeholder={draftType === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
              inputProps={{ min: 0, step: draftType === 'percentage' ? 1 : 0.01 }}
              InputProps={draftType === 'fixed'
                ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                : { endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              error={draftValue !== '' && !draftValid}
              helperText={draftValue !== '' && !draftValid
                ? (draftType === 'percentage' ? '1–100%' : 'Must be positive')
                : undefined}
              fullWidth
            />
          </Box>
          <TextField
            select
            size="small"
            label="Reason"
            value={draftReasonPick}
            onChange={(e) => setDraftReasonPick(e.target.value)}
            fullWidth
            sx={{ mb: 1.5 }}
          >
            {DISCOUNT_REASONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={draftReasonPick === 'Other' ? 'Reason detail (required)' : 'Detail (optional)'}
            value={draftReasonDetail}
            onChange={(e) => setDraftReasonDetail(e.target.value)}
            fullWidth
            sx={{ mb: 1.5 }}
            inputProps={{ maxLength: 200 }}
          />
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              color="error"
              onClick={removeDiscount}
              disabled={discountTarget === 'invoice'
                ? !invoiceDiscount
                : !(discountTarget !== null && lineItems[discountTarget]?.discountType)}
            >
              Remove
            </Button>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={closeDiscountEditor}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                onClick={applyDraftDiscount}
                disabled={!draftValid || (draftReasonPick === 'Other' && !draftReasonDetail.trim())}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default InvoiceLineItemsEditor;
